<script lang="ts">
  import { session, login } from '../../lib/auth.svelte';
  import { isPilotMode } from '../../lib/pilot';
  import { tenantCtx, loadTenantContext, clearTenantContext } from '../../lib/tenant.svelte';
  import {
    clinicPreview,
    CLINIC_ORG_OPTIONS,
    isDemoTenant as isClinicDemoTenant,
    demoTenantForOrg,
    demoAdminUser,
    orgFromDemoTenant,
    type ClinicPreviewOrg,
  } from '../../lib/clinic-preview.svelte';
  import { seedClinicByOrg } from '../../lib/clinic-demo-seed';
  import { DEMO_SEED_KEY } from '../../lib/demo-seed-shared';
  import { isClinicOrgTipo } from '../../lib/navigation';

  let seeding = $state(false);
  let seedMsg = $state<string | null>(null);
  let autoSeeded = $state(false);

  const isDemoTenant = $derived(
    isPilotMode() || isClinicDemoTenant(session.value?.tenantId),
  );

  const isClinicContext = $derived(
    !!clinicPreview.org || tenantCtx.isClinicEdition || isClinicOrgTipo(tenantCtx.orgTipo),
  );

  const visible = $derived(isDemoTenant);

  async function cargarDemo() {
    seeding = true;
    seedMsg = null;
    try {
      const org = clinicPreview.org
        ?? tenantCtx.orgTipo
        ?? orgFromDemoTenant(session.value?.tenantId)
        ?? 'clinica-medica';
      const r = await seedClinicByOrg(org);
      seedMsg = r.ok
        ? `${r.message}${r.details?.length ? ' · ' + r.details.slice(0, 4).join(' · ') : ''}`
        : r.message;
      sessionStorage.setItem(DEMO_SEED_KEY, String(org));
    } finally {
      seeding = false;
    }
  }

  $effect(() => {
    if (autoSeeded || !isDemoTenant || sessionStorage.getItem(DEMO_SEED_KEY)) return;
    autoSeeded = true;
    void cargarDemo();
  });

  async function onOrgChange(e: Event) {
    const v = (e.target as HTMLSelectElement).value;
    clinicPreview.org = v ? (v as ClinicPreviewOrg) : null;
    seedMsg = null;
    sessionStorage.removeItem(DEMO_SEED_KEY);
    if (isDemoTenant && v) {
      const tenantId = demoTenantForOrg(v as ClinicPreviewOrg);
      clearTenantContext();
      await login(tenantId, demoAdminUser(tenantId));
      await loadTenantContext(tenantId);
    }
  }
</script>

{#if visible}
  <div class="preview-bar" role="region" aria-label="Datos demo">
    <span class="tag">{isClinicContext ? 'Demo · Plan Plus' : 'Demo hospital'}</span>

    {#if isClinicContext}
      <span class="hint">8 rubros · agenda · expediente · lab · imagen · POS · FEL · bodega · tesorería · aseguradoras</span>
      <label>
        Rubro
        <select value={clinicPreview.org ?? ''} onchange={onOrgChange}>
          <option value="">— Tenant real —</option>
          {#each CLINIC_ORG_OPTIONS as o}
            <option value={o.value}>{o.emoji} {o.label}</option>
          {/each}
        </select>
      </label>
    {:else}
      <span class="hint">Camas · expediente CUI · LIS · RIS · quirófano · kárdex · bodega · tesorería · nómina · contabilidad · MSPAS · FEL</span>
    {/if}

    <button type="button" disabled={seeding} onclick={cargarDemo}>
      {seeding ? 'Sembrando…' : 'Sembrar datos demo'}
    </button>
    {#if seedMsg}<span class="msg">{seedMsg}</span>{/if}
  </div>
{/if}

<style>
  .preview-bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.65rem 1rem;
    padding: 0.65rem 1rem;
    margin-bottom: 0.85rem;
    border-radius: var(--radius-md);
    border: 1px dashed color-mix(in srgb, var(--brand) 45%, var(--border));
    background: color-mix(in srgb, var(--brand-soft) 35%, var(--bg-elevated));
    font-size: 0.85rem;
  }
  .tag {
    font-size: 0.65rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    padding: 0.2rem 0.5rem;
    border-radius: 4px;
    background: var(--brand-deep);
    color: #fff;
  }
  .hint { color: var(--text-secondary); font-size: 0.8rem; }
  label { display: flex; align-items: center; gap: 0.45rem; color: var(--text-secondary); }
  select {
    padding: 0.35rem 0.55rem;
    border-radius: 6px;
    border: 1px solid var(--border);
    background: var(--bg-surface);
    font: inherit;
    color: var(--text-primary);
  }
  button {
    padding: 0.4rem 0.85rem;
    border-radius: 999px;
    border: 0;
    background: var(--brand-strong);
    color: #fff;
    font: inherit;
    font-weight: 700;
    cursor: pointer;
  }
  button:disabled { opacity: 0.6; cursor: wait; }
  .msg { color: var(--success); font-size: 0.8rem; max-width: 36rem; }
</style>
